import { useIntl } from 'react-intl';

import type { CargoMaterial } from '#domain/CargoMaterial.ts';
import { hasDeviation } from '#domain/hasDeviation.ts';

import LabeledValue from '../form/LabeledValue.tsx';
import { packagingTranslationKeys } from '../order/CreateOrderDialog/packagingOptions.tsx';
import { DeviationValue } from './DeviationValue.tsx';

type MaterialDetailsProps = {
    material: CargoMaterial;
};

const sectionClasses = 'display-flex flex-column gap-10 bg-lightest padding-10 rounded';
const columnClasses = 'display-grid gap-10';

export const CargoMaterialDetails = ({ material }: MaterialDetailsProps) => {
    const intl = useIntl();
    const packagingLabel = intl.formatMessage({
        id: 'intl-msg:ordercom-cloud-frontend.common.load-carrier',

        defaultMessage: 'Load carrier',
    });

    const hasLoadedQuantityDeviation = hasDeviation(material.quantity.target, material.quantity.loaded);
    const hasUnloadedQuantityDeviation = hasDeviation(material.quantity.target, material.quantity.unloaded);
    const hasLoadedWeightDeviation = hasDeviation(material.weight?.target, material.weight?.loaded);
    const hasUnloadedWeightDeviation = hasDeviation(material.weight?.target, material.weight?.unloaded);

    const hasLoadedLoadingMeterDeviation = hasDeviation(material.loadingMeter?.target, material.loadingMeter?.loaded);
    const hasUnloadedLoadingMeterDeviation = hasDeviation(
        material.loadingMeter?.target,
        material.loadingMeter?.unloaded
    );

    const emptyPlaceholder = '-';
    const targetQuantity = material.quantity.target;
    const loadedQuantity = material.quantity.loaded;
    const unloadedQuantity = material.quantity.unloaded;
    const hasQuantityValues =
        targetQuantity !== undefined || loadedQuantity !== undefined || unloadedQuantity !== undefined;
    const loadedWeight = material.weight?.loaded;
    const unloadedWeight = material.weight?.unloaded;
    const targetWeight = material.weight?.target;
    const hasWeightValues = targetWeight !== undefined || loadedWeight !== undefined || unloadedWeight !== undefined;
    const loadedLoadingMeter = material.loadingMeter?.loaded;
    const unloadedLoadingMeter = material.loadingMeter?.unloaded;
    const targetLoadingMeter = material.loadingMeter?.target;
    const hasDimensions =
        material.length !== undefined || material.height !== undefined || material.width !== undefined;

    return (
        <div data-testid={`order-stop-form-${material.id}`} className={sectionClasses}>
            <div className={`${columnClasses} grid-cols-2`}>
                <LabeledValue
                    boxed
                    label={intl.formatMessage({
                        id: 'intl-msg:ordercom-cloud-frontend.material.cargo',

                        defaultMessage: 'Cargo',
                    })}
                    value={material.name}
                />
                <LabeledValue
                    boxed
                    value={
                        material.packageType in packagingTranslationKeys
                            ? intl.formatMessage({
                                  id: packagingTranslationKeys[
                                      material.packageType as keyof typeof packagingTranslationKeys
                                  ],
                              })
                            : packagingTranslationKeys.unspecified
                    }
                    label={packagingLabel}
                />
            </div>
            {hasQuantityValues && (
                <div className={`${columnClasses} grid-cols-3`}>
                    <LabeledValue
                        boxed
                        label={intl.formatMessage({
                            id: 'intl-msg:ordercom-cloud-frontend.material.target-quantity',

                            defaultMessage: 'Quantity (target)',
                        })}
                        value={targetQuantity}
                    />
                    <LabeledValue
                        boxed
                        label={intl.formatMessage({
                            id: 'intl-msg:ordercom-cloud-frontend.material.quantity-loaded',

                            defaultMessage: 'Quantity (Loaded)',
                        })}
                        value={
                            hasLoadedQuantityDeviation ? (
                                <DeviationValue
                                    targetQuantity={targetQuantity}
                                    actualQuantity={loadedQuantity}
                                    emptyPlaceholder={emptyPlaceholder}
                                />
                            ) : loadedQuantity !== undefined ? (
                                loadedQuantity
                            ) : (
                                emptyPlaceholder
                            )
                        }
                    />
                    <LabeledValue
                        boxed
                        label={intl.formatMessage({
                            id: 'intl-msg:ordercom-cloud-frontend.material.quantity-unloaded',

                            defaultMessage: 'Quantity (Unloaded)',
                        })}
                        value={
                            hasUnloadedQuantityDeviation ? (
                                <DeviationValue
                                    targetQuantity={targetQuantity}
                                    actualQuantity={unloadedQuantity}
                                    emptyPlaceholder={emptyPlaceholder}
                                />
                            ) : unloadedQuantity !== undefined ? (
                                unloadedQuantity
                            ) : (
                                emptyPlaceholder
                            )
                        }
                    />
                </div>
            )}
            {hasWeightValues && (
                <div className={`${columnClasses} grid-cols-3`}>
                    <LabeledValue
                        boxed
                        label={intl.formatMessage({
                            id: 'intl-msg:ordercom-cloud-frontend.material.target-weight',

                            defaultMessage: 'Weight (target)',
                        })}
                        value={targetWeight !== undefined ? `${targetWeight} kg` : emptyPlaceholder}
                    />
                    <LabeledValue
                        boxed
                        label={intl.formatMessage({
                            id: 'intl-msg:ordercom-cloud-frontend.material.weight-loaded',

                            defaultMessage: 'Weight (Loaded)',
                        })}
                        value={
                            hasLoadedWeightDeviation && targetWeight !== undefined ? (
                                <DeviationValue
                                    targetQuantity={targetWeight}
                                    actualQuantity={loadedWeight}
                                    emptyPlaceholder={emptyPlaceholder}
                                    unit='kg'
                                />
                            ) : loadedWeight !== undefined ? (
                                `${loadedWeight} kg`
                            ) : (
                                emptyPlaceholder
                            )
                        }
                    />
                    <LabeledValue
                        boxed
                        label={intl.formatMessage({
                            id: 'intl-msg:ordercom-cloud-frontend.material.weight-unloaded',

                            defaultMessage: 'Weight (Unloaded)',
                        })}
                        value={
                            hasUnloadedWeightDeviation && targetWeight !== undefined ? (
                                <DeviationValue
                                    targetQuantity={targetWeight}
                                    actualQuantity={unloadedWeight}
                                    emptyPlaceholder={emptyPlaceholder}
                                    unit='kg'
                                />
                            ) : unloadedWeight !== undefined ? (
                                `${unloadedWeight} kg`
                            ) : (
                                emptyPlaceholder
                            )
                        }
                    />
                </div>
            )}
            <div className={`${columnClasses} grid-cols-3`}>
                <LabeledValue
                    boxed
                    label={intl.formatMessage({
                        id: 'intl-msg:ordercom-cloud-frontend.material.target-loading-meter',

                        defaultMessage: 'Loading meter (target)',
                    })}
                    value={targetLoadingMeter !== undefined ? `${targetLoadingMeter} Ldm` : emptyPlaceholder}
                />
                <LabeledValue
                    boxed
                    label={intl.formatMessage({
                        id: 'intl-msg:ordercom-cloud-frontend.material.loading-meter-loaded',

                        defaultMessage: 'Loading meter (Loaded)',
                    })}
                    value={
                        hasLoadedLoadingMeterDeviation && targetLoadingMeter !== undefined ? (
                            <DeviationValue
                                targetQuantity={targetLoadingMeter}
                                actualQuantity={loadedLoadingMeter}
                                emptyPlaceholder={emptyPlaceholder}
                                unit='Ldm'
                            />
                        ) : loadedLoadingMeter !== undefined ? (
                            `${loadedLoadingMeter} Ldm`
                        ) : (
                            emptyPlaceholder
                        )
                    }
                />
                <LabeledValue
                    boxed
                    label={intl.formatMessage({
                        id: 'intl-msg:ordercom-cloud-frontend.material.loading-meter-unloaded',

                        defaultMessage: 'Loading meter (Unloaded)',
                    })}
                    value={
                        hasUnloadedLoadingMeterDeviation && targetLoadingMeter !== undefined ? (
                            <DeviationValue
                                targetQuantity={targetLoadingMeter}
                                actualQuantity={unloadedLoadingMeter}
                                emptyPlaceholder={emptyPlaceholder}
                                unit='Ldm'
                            />
                        ) : unloadedLoadingMeter !== undefined ? (
                            `${unloadedLoadingMeter} Ldm`
                        ) : (
                            emptyPlaceholder
                        )
                    }
                />
            </div>
            {hasDimensions && (
                <div className={`${columnClasses} grid-cols-3`}>
                    <LabeledValue
                        boxed
                        label={intl.formatMessage({
                            id: 'intl-msg:ordercom-cloud-frontend.material.length',

                            defaultMessage: 'Length (cm)',
                        })}
                        value={material.length ?? emptyPlaceholder}
                    />
                    <LabeledValue
                        boxed
                        label={intl.formatMessage({
                            id: 'intl-msg:ordercom-cloud-frontend.material.height',

                            defaultMessage: 'Height (cm)',
                        })}
                        value={material.height ?? emptyPlaceholder}
                    />
                    <LabeledValue
                        boxed
                        label={intl.formatMessage({
                            id: 'intl-msg:ordercom-cloud-frontend.material.width',

                            defaultMessage: 'Width (cm)',
                        })}
                        value={material.width ?? emptyPlaceholder}
                    />
                </div>
            )}
            {material.comment && (
                <div className={`${columnClasses} grid-cols-1`}>
                    {material.comment.loading && (
                        <LabeledValue
                            label={intl.formatMessage({
                                id: 'intl-msg:ordercom-cloud-frontend.orders.details.stop.form.input.comment.driver.loading',

                                defaultMessage: 'Driver’s comment during loading',
                            })}
                            className='padding-x-10 padding-y-5'
                            icon='comment'
                            value={material.comment.loading ?? emptyPlaceholder}
                        />
                    )}
                    {material.comment.unloading && (
                        <LabeledValue
                            label={intl.formatMessage({
                                id: 'intl-msg:ordercom-cloud-frontend.orders.details.stop.form.input.comment.driver.unloading',

                                defaultMessage: 'Driver’s comment during unloading',
                            })}
                            className='padding-x-10 padding-y-5'
                            icon='comment'
                            value={material.comment.unloading ?? emptyPlaceholder}
                        />
                    )}
                </div>
            )}
        </div>
    );
};
